import { useCallback, useEffect, useMemo, useState } from "react";

import { createLeisureVideo, deleteLeisureVideo, fetchLeisureVideos, updateLeisureVideo } from "@/features/admin/api";
import type { AdminLeisureVideo } from "@/features/admin/types";
import { useAdminSession } from "@/features/admin/useAdminSession";
import PaginationControls from "./PaginationControls";

const EMPTY_FORM = {
  title: "",
  sourceUrl: "",
  statut: "publie" as AdminLeisureVideo["statut"]
};
const LEISURE_VIDEOS_PER_PAGE = 5;

function formatDateTime(value: string) {
  return new Date(value).toLocaleString("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short"
  });
}

export default function LoisirsTab() {
  const { request } = useAdminSession();
  const [videos, setVideos] = useState<AdminLeisureVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingVideo, setEditingVideo] = useState<AdminLeisureVideo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [currentPage, setCurrentPage] = useState(1);

  const loadVideos = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const nextVideos = await fetchLeisureVideos(request);
      setVideos(nextVideos);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les vidéos loisirs";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    void loadVideos();
  }, [loadVideos]);

  const publishedCount = useMemo(() => videos.filter((video) => video.statut === "publie").length, [videos]);
  const liveCount = useMemo(() => videos.filter((video) => video.isLive).length, [videos]);
  const totalPages = Math.max(1, Math.ceil(videos.length / LEISURE_VIDEOS_PER_PAGE));
  const paginatedVideos = useMemo(() => {
    const startIndex = (currentPage - 1) * LEISURE_VIDEOS_PER_PAGE;
    return videos.slice(startIndex, startIndex + LEISURE_VIDEOS_PER_PAGE);
  }, [currentPage, videos]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const resetForm = () => {
    setEditingVideo(null);
    setForm(EMPTY_FORM);
  };

  const handleOpenEdit = (video: AdminLeisureVideo) => {
    setEditingVideo(video);
    setForm({
      title: video.title,
      sourceUrl: video.sourceUrl,
      statut: video.statut
    });
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.sourceUrl.trim()) {
      setError("Le titre et le lien YouTube sont obligatoires.");
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingVideo) {
        await updateLeisureVideo(request, editingVideo.id, {
          title: form.title.trim(),
          sourceUrl: form.sourceUrl.trim(),
          statut: form.statut
        });
        setSuccess("La vidéo loisirs a bien été mise à jour.");
      } else {
        await createLeisureVideo(request, {
          title: form.title.trim(),
          sourceUrl: form.sourceUrl.trim(),
          statut: form.statut
        });
        setSuccess("La vidéo loisirs a bien été publiée.");
      }

      resetForm();
      await loadVideos();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible d'enregistrer cette vidéo";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (video: AdminLeisureVideo) => {
    setError(null);
    setSuccess(null);

    try {
      await updateLeisureVideo(request, video.id, {
        statut: video.statut === "publie" ? "masque" : "publie"
      });
      setSuccess(video.statut === "publie" ? "La vidéo a été masquée." : "La vidéo a été republiée.");
      await loadVideos();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de changer la visibilité";
      setError(message);
    }
  };

  const handleDelete = async (videoId: string) => {
    setDeletingId(videoId);
    setError(null);
    setSuccess(null);

    try {
      await deleteLeisureVideo(request, videoId);
      setSuccess("La vidéo loisirs a été supprimée.");
      if (editingVideo?.id === videoId) {
        resetForm();
      }
      await loadVideos();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de supprimer cette vidéo";
      setError(message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#1A1A1A] md:text-2xl">Loisirs vidéo</h1>
          <p className="mt-1 text-sm text-gray-500">
            Publie ici des liens YouTube ou YouTube Kids pour alimenter l'espace loisirs public.
          </p>
        </div>
        <button
          onClick={() => void loadVideos()}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:border-[#E8521A] hover:text-[#E8521A]"
        >
          <i className="ri-refresh-line"></i>
          Actualiser
        </button>
      </div>

      {error ? <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div> : null}
      {success ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{success}</div> : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-gray-100 bg-white p-4">
          <div className="text-2xl font-bold text-[#1A1A1A]">{videos.length}</div>
          <div className="mt-1 text-sm text-gray-500">Vidéos enregistrées</div>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-4">
          <div className="text-2xl font-bold text-[#E8521A]">{publishedCount}</div>
          <div className="mt-1 text-sm text-gray-500">Publiées</div>
        </div>
        <div className="rounded-2xl border border-gray-100 bg-white p-4">
          <div className="text-2xl font-bold text-red-500">{liveCount}</div>
          <div className="mt-1 text-sm text-gray-500">Lives détectés</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[420px_minmax(0,1fr)]">
        <div className="rounded-[28px] border border-gray-100 bg-white p-6 shadow-[0_24px_80px_rgba(17,24,39,0.05)]">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-[#E8521A]">
              <i className="ri-youtube-line text-2xl"></i>
            </div>
            <div>
              <h2 className="font-bold text-[#1A1A1A]">{editingVideo ? "Modifier la vidéo" : "Publier une vidéo"}</h2>
              <p className="text-sm text-gray-500">Colle le lien et définis le titre affiché au public.</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Titre visible</label>
              <input
                type="text"
                value={form.title}
                onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                placeholder="Ex: Comptines éducatives SONATEL"
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-[#E8521A] focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Lien YouTube / YouTube Kids</label>
              <input
                type="url"
                value={form.sourceUrl}
                onChange={(event) => setForm((current) => ({ ...current, sourceUrl: event.target.value }))}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-[#E8521A] focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Visibilité</label>
              <select
                value={form.statut}
                onChange={(event) => setForm((current) => ({ ...current, statut: event.target.value as AdminLeisureVideo["statut"] }))}
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-[#E8521A] focus:outline-none transition-colors"
              >
                <option value="publie">Publier tout de suite</option>
                <option value="masque">Garder masqué</option>
              </select>
            </div>

            <div className="rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-[#9A3E18]">
              Les liens acceptés sont ceux de YouTube et YouTube Kids. Les lives YouTube sont aussi pris en charge.
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={submitting}
                className="flex-1 rounded-xl bg-[#E8521A] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Enregistrement..." : editingVideo ? "Mettre à jour" : "Publier"}
              </button>
              {editingVideo ? (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
                >
                  Annuler
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="h-36 animate-pulse rounded-2xl bg-gray-100"></div>
              ))}
            </div>
          ) : videos.length > 0 ? (
            paginatedVideos.map((video) => (
              <div key={video.id} className="overflow-hidden rounded-[28px] border border-gray-100 bg-white shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)]">
                  <div className="relative h-full min-h-[160px] bg-gray-100">
                    <img src={video.thumbnailUrl} alt={video.title} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                    <div className="absolute left-4 top-4 flex flex-wrap gap-2">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${video.statut === "publie" ? "bg-emerald-500 text-white" : "bg-gray-900/70 text-white"}`}>
                        {video.statut === "publie" ? "Publié" : "Masqué"}
                      </span>
                      {video.isLive ? (
                        <span className="rounded-full bg-red-500 px-3 py-1 text-xs font-semibold text-white">Live</span>
                      ) : null}
                    </div>
                  </div>

                  <div className="p-5">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-lg font-bold text-[#1A1A1A]">{video.title}</h3>
                          <span className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-medium text-[#E8521A]">{video.provider}</span>
                        </div>
                        <p className="mt-2 break-all text-sm text-gray-500">{video.sourceUrl}</p>
                        <p className="mt-3 text-xs text-gray-400">Ajoutée le {formatDateTime(video.createdAt)}</p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(video)}
                          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                            video.statut === "publie"
                              ? "border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                              : "border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          }`}
                        >
                          <i className={video.statut === "publie" ? "ri-eye-off-line" : "ri-eye-line"}></i>
                          {video.statut === "publie" ? "Masquer" : "Publier"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenEdit(video)}
                          className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                        >
                          <i className="ri-edit-line"></i>
                          Modifier
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete(video.id)}
                          disabled={deletingId === video.id}
                          className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <i className={deletingId === video.id ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"}></i>
                          {deletingId === video.id ? "Suppression..." : "Supprimer"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-[28px] border border-dashed border-gray-200 bg-white px-6 py-16 text-center">
              <i className="ri-movie-2-line text-5xl text-gray-300"></i>
              <p className="mt-4 text-sm text-gray-500">Aucune vidéo loisirs n'a encore été publiée.</p>
            </div>
          )}

          {!loading && videos.length > LEISURE_VIDEOS_PER_PAGE ? (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemLabel="vidéos loisirs"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
